import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { SPACE, FONT, RADIUS } from '@/constants/designTokens';
import { TYPO } from '@/constants/typography';
import { LucideIcon } from 'lucide-react-native';

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  actionLabel: string;
  onAction: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
}

export default function EmptyState({
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
}: EmptyStateProps) {
  const { theme } = useTheme();

  return (
    <View style={styles.container}>
      <View style={[styles.iconCircle, { backgroundColor: theme.colors.surfaceVariant }]}>
        <Icon size={48} color={theme.colors.textSecondary} />
      </View>
      <Text style={[styles.title, { color: theme.colors.text }]}>{title}</Text>
      <Text style={[styles.description, { color: theme.colors.textSecondary }]}>
        {description}
      </Text>
      <TouchableOpacity
        style={[styles.primaryButton, { backgroundColor: theme.colors.accent }]}
        onPress={onAction}>
        <Text style={[styles.buttonText, { color: theme.colors.accentForeground }]}>{actionLabel}</Text>
      </TouchableOpacity>
      {secondaryActionLabel && onSecondaryAction && (
        <TouchableOpacity style={styles.secondaryButton} onPress={onSecondaryAction}>
          <Text style={[styles.secondaryButtonText, { color: theme.colors.accent }]}>
            {secondaryActionLabel}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: SPACE.xl,
  },
  iconCircle: {
    width: 100,
    height: 100,
    borderRadius: RADIUS.sm,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: SPACE.lg,
  },
  title: {
    fontSize: FONT.xl,
    fontFamily: TYPO.heading.bold,
    textAlign: 'center',
    marginBottom: SPACE.base,
  },
  description: {
    fontSize: FONT.md,
    fontFamily: TYPO.body.regular,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: SPACE.xl,
    paddingHorizontal: SPACE.md,
  },
  primaryButton: {
    paddingVertical: SPACE.md,
    paddingHorizontal: SPACE.xl,
    borderRadius: RADIUS.none,
    minWidth: 200,
    alignItems: 'center',
  },
  buttonText: {
    fontSize: FONT.md,
    fontFamily: TYPO.heading.bold,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  secondaryButton: {
    marginTop: SPACE.md,
    paddingVertical: SPACE.base,
  },
  secondaryButtonText: {
    fontSize: FONT.md,
    fontFamily: TYPO.body.semibold,
  },
});
